import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import type { IPatient } from '../../../../../../common/interfaces/patient';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { ICustomControlData } from './customControlData';
import { translateKey } from '../../../../../translate';
import { FormContextProvider } from '../../../../elements/formContext';
import { TextAreaField } from '../../../../fields/TextArea';
import { BmiAddEdit } from '../../utils/v1/BmiAddEdit';
import { AdviesBijAfwijkendeBeoordelingAddEdit } from './AdviesBijAfwijkendeBeoordelingAddEdit';
import { GeslachtAddEdit } from './GeslachtAddEdit';
import { IndicatieAddEdit } from './IndicatieAddEdit';
import { MedicatieAddEdit } from './MedicatieAddEdit';
import { OpnameduurAddEdit } from './OpnameduurAddEdit';
import { PacemakerAddEdit } from './PacemakerAddEdit';
import { ProcedureTypeAddEdit } from './ProcedureTypeAddEdit';

interface ITcohV2ProcedureAddEditCustomControlFieldsProps {
    customControlData: ICustomControlData;
    errors: ValidationErrors;
    handleChangeCustomControl: (customControl: Partial<ICustomControlData>) => Promise<void>;
    patient: IPatient;
    validateOneCustomControl: (name: string) => Promise<void>;
}

export function TcohV2ProcedureAddEditCustomControlFields(props: ITcohV2ProcedureAddEditCustomControlFieldsProps) {
    const {
        frontendSettings: { isTcohMkcFeatureEnabled },
    } = useFrontendSettings();

    return (
        <FormContextProvider
            value={{
                errors: props.errors,
                item: props.customControlData,
                onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                onDoValidate: async (key) => await props.validateOneCustomControl(key),
                placeholderGroupName: 'procedure',
            }}
        >
            <GeslachtAddEdit {...props} />
            <ProcedureTypeAddEdit {...props} />
            <OpnameduurAddEdit
                customControlData={props.customControlData}
                errors={props.errors}
                onChange={async (key, value) => await props.handleChangeCustomControl({ [key]: value })}
                onDoValidate={async (key) => await props.validateOneCustomControl(key)}
            />
            <IndicatieAddEdit {...props} />
            <MedicatieAddEdit {...props} />
            <PacemakerAddEdit {...props} />
            <BmiAddEdit {...props} />
            <TextAreaField
                error={props.errors.get('customControlData.bekendeEcgAfwijkingen')}
                fieldKey="bekendeEcgAfwijkingen"
                hasErrors={props.errors.has('customControlData.bekendeEcgAfwijkingen')}
                label="Bekende ECG afwijkingen"
                placeholder="Welke afwijkingen zijn gezien bij de patiënt..."
                value={props.customControlData.bekendeEcgAfwijkingen || null}
            />
            {isTcohMkcFeatureEnabled === false && (
                <TextAreaField
                    error={props.errors.get('customControlData.vraagstelling')}
                    fieldKey="vraagstelling"
                    hasErrors={props.errors.has('customControlData.vraagstelling')}
                    label="Vraag"
                    placeholder={translateKey('request.askQuestionToSpecialist')}
                    value={props.customControlData.vraagstelling || null}
                />
            )}
            <AdviesBijAfwijkendeBeoordelingAddEdit {...props} />
        </FormContextProvider>
    );
}
